// ============================================
// MEN'S SEXUAL HEALTH FOLLOW-UP QUESTIONNAIRE DATA
// ============================================
// Based on Beluga Health "Men's Sexual Health Follow Up Questionnaire" (2025).
// Visit Type: mensSexualHealthFollowup
// No labs. Identical structure to womenSexualHealthFollowUpData (same flow,
// conditionals, and disqualifiers) — only the main consent body differs (men's
// PT-141 / Oxytocin / Apomorphine / PDE5i consent).
//
// Color legend: BLACK = Q/A, BLUE = conditional, PURPLE = named-field, RED =
// disqualifying, PINK = product routing (none — follow-ups continue the parent's
// patientPreference).
//
// See docs/BACKEND_MENS_SEXUAL_HEALTH_FOLLOWUP_PRD.md for the canonical contract.

const TRUTHFULNESS_CONSENT_BODY = `Please attest to the following confirming that all information you have provided to us is true and complete.

I verify that I am the patient and that I have answered the questions asked in this intake form. I confirm that I have reviewed and understood all the questions and that my responses are true, accurate, and complete to the best of my knowledge. I understand that providing inaccurate or incomplete information may affect the safety and appropriateness of my treatment.`;

const MAIN_CONSENT_BODY = `Consent for PT-141, Oxytocin, Apomorphine, and PDE5 Inhibitors (Men's Libido and Erectile Function)

This consent and product education explains the use of compounded PT-141 (bremelanotide), oxytocin, and apomorphine, as well as phosphodiesterase type 5 inhibitors (PDE5i). Limited data exist regarding the safety and effectiveness of PT-141, oxytocin, and apomorphine in men, and standardized dosing guidelines have not been established.

Purpose and Benefits
- PT-141 (Bremelanotide): stimulates melanocortin receptors in the brain, which may improve erectile function, reliability of erections, and sexual satisfaction.
- Oxytocin: often called the "bonding hormone," it may promote intimacy, trust, and sexual desire.
- Apomorphine: a dopamine agonist that may improve erectile response by acting on central nervous system pathways; may be beneficial in men who do not respond adequately to PDE5 inhibitors.
- PDE5 Inhibitors (Sildenafil, Tadalafil, Vardenafil, Avanafil): improve erectile function by increasing blood flow to the penis during sexual stimulation.

Potential Risks and Side Effects vary by medication and may include nausea, headache, flushing, dizziness, nasal congestion, visual changes, back pain, injection-site reactions, and possible skin hyperpigmentation. Rare but serious risks (all): significant or sustained blood pressure changes (which may increase risk of stroke or heart attack), severe allergic reactions, and noticeable changes in mood or behavior. PDE5 inhibitors carry a rare risk of priapism (erection >4 hours).

Do not use PDE5 inhibitors if you take nitrates for chest pain (angina), use recreational "poppers" (amyl nitrate or nitrite), or have severe heart or liver problems, very low blood pressure, or recent stroke/heart attack. Seek emergency care immediately for chest pain, trouble breathing, extreme dizziness, a prolonged erection, or signs of a severe allergic reaction.

Monitoring: attend all scheduled follow-up visits, complete any recommended labs or blood pressure checks, and report new or worsening symptoms. Alternatives include lifestyle modification, sex/relationship therapy, and other medical options (hormone therapy, vacuum erection devices, penile injections, suppositories, or surgical implants).

By selecting below, you confirm that you have read and understood this information, are aware of the potential benefits, risks, and alternatives, understand that use of PT-141, oxytocin, and apomorphine is off-label and not FDA-approved for men, agree to follow your provider's instructions and attend all follow-ups, and understand you may withdraw from treatment at any time.`;

// ---------- PURPLE textarea slides (extracted to named fields, prefilled) ----------

const medicalConditionsQuestion = {
  id: 'medical-conditions',
  type: 'textarea-prefilled',
  question: 'Please identify all your current medical conditions',
  fieldMapping: 'medicalConditions',
  placeholder: 'e.g., Hypertension, Diabetes, etc.',
  required: true,
};

const selfReportedMedsQuestion = {
  id: 'self-reported-meds',
  type: 'textarea-prefilled',
  question: 'Please list all your current medications including dosages',
  fieldMapping: 'selfReportedMeds',
  placeholder: 'e.g., Lisinopril 10mg daily, Atorvastatin 20mg daily…',
  required: true,
};

const allergiesQuestion = {
  id: 'allergies',
  type: 'textarea-prefilled',
  question: 'Please list all of your known allergies',
  fieldMapping: 'allergies',
  placeholder: 'e.g., Penicillin (rash), Shellfish (anaphylaxis)…',
  required: true,
};

// ---------- Health / medication changes ----------

const healthChangesQuestion = {
  id: 'health-changes',
  type: 'single-select',
  question:
    'Have there been any health or medication changes, ED visits or hospitalizations since your last check-in?',
  options: [
    { label: 'No', value: 'no' },
    { label: 'Yes', value: 'yes' },
  ],
};

const healthChangesDetailsQuestion = {
  id: 'health-changes-details',
  type: 'textarea',
  question: 'Please tell us more about the changes in your health',
  showIf: { field: 'health-changes', value: 'yes' },
  placeholder: 'Describe your health changes…',
  required: true,
};

const medicationResponseQuestion = {
  id: 'medication-response',
  type: 'single-select',
  question: 'How are you doing on the current medication?',
  options: [
    { label: 'Well', value: 'well' },
    { label: 'Not well', value: 'not-well' },
  ],
};

const medicationResponseDetailsQuestion = {
  id: 'medication-response-details',
  type: 'textarea',
  question: 'Please tell us more about what you are experiencing',
  showIf: { field: 'medication-response', value: 'not-well' },
  placeholder: 'Describe what you are experiencing…',
  required: true,
};

const sideEffectsQuestion = {
  id: 'side-effects',
  type: 'single-select',
  question: 'Have you experienced any side effects from the current medication?',
  options: [
    { label: 'No', value: 'no' },
    { label: 'Yes', value: 'yes' },
  ],
};

const sideEffectsDetailsQuestion = {
  id: 'side-effects-details',
  type: 'textarea',
  question: 'Please tell us more about the side effects you are experiencing',
  showIf: { field: 'side-effects', value: 'yes' },
  placeholder: 'Describe the side effects…',
  required: true,
};

// ---------- Blood pressure branch ----------

const highBpQuestion = {
  id: 'high-bp',
  type: 'single-select',
  question: 'Do you have high blood pressure?',
  options: [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
  ],
};

const bpControlledQuestion = {
  id: 'bp-controlled',
  type: 'single-select',
  question: 'Is your blood pressure typically well controlled?',
  showIf: { field: 'high-bp', value: 'yes' },
  options: [
    { label: 'Yes', value: 'yes' },
    {
      label: 'No',
      value: 'no',
      disqualifies: true,
      disqualifyReason:
        'Because your blood pressure is not well controlled, these medications are not safe for you right now. Please follow up with your primary care provider.',
    },
  ],
};

const bpRecentReadingQuestion = {
  id: 'bp-recent-reading',
  type: 'textarea',
  question:
    'Please provide a recent blood pressure reading taken within the last 4-6 weeks',
  showIf: { field: 'bp-controlled', value: 'yes' },
  placeholder: 'e.g., 118/76',
  required: true,
};

// ---------- Treatment preference ----------

const treatmentPreferenceQuestion = {
  id: 'treatment-preference',
  type: 'single-select',
  question:
    'Would you like to continue your current treatment, change the dose, or change the medication?',
  options: [
    { label: "I'm doing well and I would like to continue what I'm on", value: 'continue' },
    { label: 'I would like to change the medication if there are other options', value: 'change-medication' },
    { label: "I'm not sure, have the provider reach out to discuss further", value: 'unsure' },
  ],
};

const additionalQuestionsQuestion = {
  id: 'additional-questions',
  type: 'textarea',
  question: 'What other information or questions do you have for the doctor?',
  placeholder: 'Type your questions or concerns here…',
  required: false,
};

const truthfulnessConsentQuestion = {
  id: 'truthfulness-consent',
  type: 'consent-dropdown',
  question: 'Truthfulness Attestation',
  consentText: TRUTHFULNESS_CONSENT_BODY,
  options: [
    { label: 'I have read the above information and I do consent and wish to move forward', value: 'consent' },
    { label: 'No — I do not wish to continue with treatment', value: 'decline', disqualifies: true },
  ],
};

const mainConsentQuestion = {
  id: 'main-consent',
  type: 'consent-dropdown',
  question: 'PT-141, Oxytocin, Apomorphine, and PDE5 Inhibitors — Informed Consent',
  consentText: MAIN_CONSENT_BODY,
  options: [
    { label: 'I have read the above information, I understand the risks, and I would like to proceed.', value: 'consent' },
    { label: 'I have read the above information, and I do NOT wish to proceed.', value: 'decline', disqualifies: true },
  ],
};

const checkoutQuestion = {
  id: 'checkout',
  type: 'checkout',
  question: 'Review treatment and pay',
};

// ---------- Question registry ----------

export const mensSexualHealthFollowUpQuestions = {
  medicalConditions: medicalConditionsQuestion,
  selfReportedMeds: selfReportedMedsQuestion,
  allergies: allergiesQuestion,
  healthChanges: healthChangesQuestion,
  healthChangesDetails: healthChangesDetailsQuestion,
  medicationResponse: medicationResponseQuestion,
  medicationResponseDetails: medicationResponseDetailsQuestion,
  sideEffects: sideEffectsQuestion,
  sideEffectsDetails: sideEffectsDetailsQuestion,
  highBp: highBpQuestion,
  bpControlled: bpControlledQuestion,
  bpRecentReading: bpRecentReadingQuestion,
  treatmentPreference: treatmentPreferenceQuestion,
  additionalQuestions: additionalQuestionsQuestion,
  truthfulnessConsent: truthfulnessConsentQuestion,
  mainConsent: mainConsentQuestion,
  checkout: checkoutQuestion,
};

export const mensSexualHealthFollowUpFlow = [
  'medical-conditions',
  'self-reported-meds',
  'allergies',
  'health-changes',
  'health-changes-details',
  'medication-response',
  'medication-response-details',
  'side-effects',
  'side-effects-details',
  'high-bp',
  'bp-controlled',
  'bp-recent-reading',
  'treatment-preference',
  'additional-questions',
  'truthfulness-consent',
  'main-consent',
  'checkout',
];

export const getMensSexualHealthFollowUpQuestionById = (id) =>
  Object.values(mensSexualHealthFollowUpQuestions).find((q) => q.id === id);

// BLUE conditionals:
//   health-changes-details        ← health-changes='yes'
//   medication-response-details   ← medication-response='not-well'
//   side-effects-details          ← side-effects='yes'
//   bp-controlled                 ← high-bp='yes'   (No → disqualifies)
//   bp-recent-reading             ← bp-controlled='yes'
export const buildDynamicMensSexualHealthFollowUpFlow = (answers /* , userData */) => {
  const flow = [];
  for (const questionId of mensSexualHealthFollowUpFlow) {
    const question = getMensSexualHealthFollowUpQuestionById(questionId);
    if (!question) continue;

    if (question.showIf) {
      const { field, value, includes } = question.showIf;
      const current = answers[field];
      if (includes !== undefined) {
        if (!Array.isArray(current) || !current.includes(includes)) continue;
      } else if (current !== value) {
        continue;
      }
    }

    flow.push(question);
  }
  return flow;
};
